"use client";

import {
  useEffect,
  useState,
} from "react";

const loadedVersion =
  process.env.NEXT_PUBLIC_MARGINGLOW_BUILD_VERSION ||
  "development";

export default function StaleVersionGuard() {
  const [
    availableVersion,
    setAvailableVersion,
  ] = useState("");
  const [dismissed, setDismissed] =
    useState(false);

  useEffect(() => {
    let checking = false;
    let stopped = false;

    async function checkVersion() {
      if (
        checking ||
        stopped ||
        document.visibilityState ===
          "hidden"
      ) {
        return;
      }

      checking = true;

      try {
        const response = await fetch(
          "/api/app-version",
          {
            cache: "no-store",
            headers: {
              "Cache-Control":
                "no-cache",
            },
          },
        );

        if (!response.ok) {
          return;
        }

        const data =
          (await response.json()) as {
            version?: string;
          };

        if (
          !stopped &&
          data.version &&
          data.version !== loadedVersion
        ) {
          setAvailableVersion(
            data.version,
          );
        }
      } catch {
        // Network failures must never
        // interrupt the user's work.
      } finally {
        checking = false;
      }
    }

    const interval =
      window.setInterval(
        checkVersion,
        60000,
      );

    const initialCheck =
      window.setTimeout(
        checkVersion,
        1500,
      );

    function checkVisibleVersion() {
      if (
        document.visibilityState ===
        "visible"
      ) {
        checkVersion();
      }
    }

    window.addEventListener(
      "focus",
      checkVersion,
    );

    document.addEventListener(
      "visibilitychange",
      checkVisibleVersion,
    );

    return () => {
      stopped = true;

      window.clearInterval(interval);
      window.clearTimeout(
        initialCheck,
      );

      window.removeEventListener(
        "focus",
        checkVersion,
      );

      document.removeEventListener(
        "visibilitychange",
        checkVisibleVersion,
      );
    };
  }, []);

  function installUpdate() {
    if (!availableVersion) {
      return;
    }

    const url =
      new URL(window.location.href);

    url.searchParams.set(
      "_mgv",
      availableVersion,
    );

    window.location.replace(
      url.toString(),
    );
  }

  if (
    !availableVersion ||
    dismissed
  ) {
    return null;
  }

  return (
    <aside
      role="status"
      aria-live="polite"
      className="fixed inset-x-4 bottom-4 z-[200] mx-auto max-w-3xl rounded-3xl border border-[#61e6dc] bg-[#092723] p-5 text-white shadow-[0_20px_60px_rgba(9,39,35,0.32)]"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-normal uppercase tracking-[0.16em] text-[#61e6dc]">
            Update ready
          </p>

          <p className="mt-2 text-sm font-light leading-6 text-[#d7e5df]">
            A newer version of MarginGlow is ready.
            Finish or save anything you are editing,
            then update when it suits you. You will
            remain signed in.
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            type="button"
            onClick={() =>
              setDismissed(true)
            }
            className="rounded-2xl border border-white/30 bg-transparent px-4 py-2 text-sm font-normal text-white transition hover:bg-white/10"
          >
            Keep working
          </button>

          <button
            type="button"
            onClick={installUpdate}
            className="rounded-2xl bg-[#61e6dc] px-4 py-2 text-sm font-normal text-[#092723] transition hover:bg-white"
          >
            Update now
          </button>
        </div>
      </div>
    </aside>
  );
}
